/** Contexto de "Mis datos": guardar borrador/foto y empezar de cero. En modo ejemplo todo es en memoria. */
import { createContext, useContext, type ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { api, ApiError } from "@/lib/api";
import type { MemberRow, SnapshotRow } from "@/types/db";
import { useBorrador } from "./persistencia/useBorrador";
import { borrarLocal } from "./persistencia/local";
import { useDatos } from "./store/store";

export interface DatosApi {
  modo: "real" | "demo";
  cargando: boolean;
  guardarBorrador: () => Promise<boolean>;
  empezarDeCero: () => Promise<void>;
  /** Guarda los datos actuales como la foto vigente. Devuelve null si salió bien o el mensaje de error. */
  guardarFoto: (label?: string) => Promise<string | null>;
}
const Ctx = createContext<DatosApi | null>(null);

export function DatosProvider({ member, snapshotActual, snapshotCargando, children }: { member: MemberRow; snapshotActual: { row: SnapshotRow } | null | undefined; snapshotCargando: boolean; children: ReactNode }) {
  const b = useBorrador(member, snapshotActual, snapshotCargando);
  const qc = useQueryClient();
  async function guardarFoto(label?: string): Promise<string | null> {
    const st = useDatos.getState();
    try {
      const u = st.meta.seedUpload;
      const r = await api<{ snapshotId: string }>("/api/snapshots", { method: "POST", json: { modelVersion: st.inputs.version, inputs: st.inputs, label: label ?? "Desde la app", source: "form", upload: u ? { ...u } : null } });
      useDatos.getState().setMeta({ source: "snapshot", seedSnapshotId: r.snapshotId, seedUpload: null });
      // El borrador queda igual a la foto recién guardada.
      try { await api("/api/drafts", { method: "PUT", json: { modelVersion: st.inputs.version, source: "snapshot", seedSnapshotId: r.snapshotId, inputs: st.inputs } }); useDatos.getState().setMeta({ revisionGuardada: useDatos.getState().meta.revisionLocal, guardado: "guardado", lastSavedAt: new Date().toISOString() }); borrarLocal(member.id); } catch { /* el autosave lo reintenta */ }
      await qc.invalidateQueries({ queryKey: ["snapshot"] });
      await qc.invalidateQueries({ queryKey: ["draft"] });
      return null;
    } catch (e) {
      return e instanceof ApiError ? e.message : "No pude guardar tu foto. Revisa tu conexión e intenta de nuevo.";
    }
  }
  return <Ctx.Provider value={{ modo: "real", cargando: b.cargando, guardarBorrador: b.guardarBorrador, empezarDeCero: b.empezarDeCero, guardarFoto }}>{children}</Ctx.Provider>;
}

/** Proveedor para el modo ejemplo: nada se guarda. */
export function DatosDemoProvider({ children }: { children: ReactNode }) {
  const nada = async () => false;
  return <Ctx.Provider value={{ modo: "demo", cargando: false, guardarBorrador: nada, empezarDeCero: async () => undefined, guardarFoto: async () => "En el ejemplo no se guarda nada. Entra con tu cuenta para guardar tu foto." }}>{children}</Ctx.Provider>;
}

export function useDatosApi(): DatosApi {
  const c = useContext(Ctx);
  if (!c) throw new Error("useDatosApi fuera de DatosProvider");
  return c;
}
